import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { AuditingOrderItem, AuditingOrdersRequest, AuditingOrdersResponse } from '../../types/reporting';

const getAuditingOrders = async (
    sessionId: string,
    input: AuditingOrdersRequest
): Promise<AuditingOrdersResponse> => {
    const { data } = await axios.post(
        `${API_URL}/reporting/auditing/orders`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

// Options for server-side filters/pagination/search/sort
export interface UseAuditingOrdersOptions {
    // Server-side filters to reduce data
    locationIds?: string[];
    insuranceIds?: string[];
    makes?: string[];
    /** Explicit order IDs (e.g. from pivot drilldown) — when set, overrides other domain filters. */
    orderIds?: string[];
    /** Matches global reporting timeframe (e.g. last_7_days, custom). */
    preset?: string;
    excludeEvs?: boolean;
    page?: number;
    limit?: number;
    searchQuery?: string;
    sortBy?: string | null;
    sortOrder?: 'asc' | 'desc';
    rangeFilters?: Record<string, { min: string; max: string }>;
    /** When false, keep cache but do not fetch (inactive reporting tab). */
    enabled?: boolean;
}

const stableIdsKey = (ids?: string[]) =>
    ids?.length ? JSON.stringify([...ids].sort()) : '';
const stableObjectKey = (obj?: Record<string, unknown>) =>
    obj ? JSON.stringify(Object.keys(obj).sort().reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = obj[key];
        return acc;
    }, {})) : '';

/**
 * Fetches every auditing order matching the given filters by paging through the
 * endpoint in batches. Used for "export all" flows where the on-screen pagination
 * should be ignored.
 */
export const fetchAllAuditingOrders = async (
    sessionId: string,
    startDate: string,
    endDate: string,
    options?: Omit<UseAuditingOrdersOptions, 'page' | 'limit'>,
    batchSize = 200
): Promise<AuditingOrderItem[]> => {
    const all: AuditingOrderItem[] = [];
    let offset = 0;
    // Safety cap to avoid an unbounded loop if the server keeps returning full batches.
    for (let i = 0; i < 500; i++) {
        const res = await getAuditingOrders(sessionId, {
            startDate,
            endDate,
            preset: options?.preset,
            locationIds: options?.locationIds,
            insuranceIds: options?.insuranceIds,
            makes: options?.makes,
            orderIds: options?.orderIds,
            excludeEvs: options?.excludeEvs,
            limit: batchSize,
            offset,
            searchQuery: options?.searchQuery,
            sortBy: options?.sortBy ?? undefined,
            sortOrder: options?.sortOrder,
            rangeFilters: options?.rangeFilters,
        });
        const batch = res.orders ?? [];
        all.push(...batch);
        // Stop when the server returns a short/empty page; advance by actual batch size
        // so a clamped limit can't skip rows or exit after the first page.
        if (batch.length === 0 || batch.length < batchSize) break;
        offset += batch.length;
    }
    return all;
};

export const useAuditingOrders = (
    startDate: string,
    endDate: string,
    options?: UseAuditingOrdersOptions
) => {
    const sessionContext = useContext(SessionContext);
    const page = options?.page ?? 1;
    const limit = options?.limit ?? 51;
    const offset = (page - 1) * limit;

    return useQuery({
        queryKey: [
            'reporting',
            'auditing',
            'orders',
            startDate,
            endDate,
            options?.preset ?? '',
            stableIdsKey(options?.locationIds),
            stableIdsKey(options?.insuranceIds),
            stableIdsKey(options?.makes),
            stableIdsKey(options?.orderIds),
            options?.excludeEvs ?? false,
            page,
            limit,
            options?.searchQuery ?? '',
            options?.sortBy ?? '',
            options?.sortOrder ?? '',
            stableObjectKey(options?.rangeFilters),
        ],
        queryFn: () =>
            getAuditingOrders(sessionContext?.sessionId ?? '', {
                startDate,
                endDate,
                preset: options?.preset,
                locationIds: options?.locationIds,
                insuranceIds: options?.insuranceIds,
                makes: options?.makes,
                orderIds: options?.orderIds,
                excludeEvs: options?.excludeEvs,
                limit,
                offset,
                searchQuery: options?.searchQuery,
                sortBy: options?.sortBy ?? undefined,
                sortOrder: options?.sortOrder,
                rangeFilters: options?.rangeFilters,
            }),
        enabled: (options?.enabled ?? true) && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
    });
};
